/**
 * "Next hour" (docs/nowcast-plan.md, Stage B): which bars to draw and the one
 * sentence above them. Plain TypeScript; `NextHour.svelte` draws what these
 * return.
 *
 * The server traces the radar seventy minutes ahead of its frame; the frame
 * is three to five minutes old by the time anybody reads it, so the hour is
 * cut out of that from *now*. The sentence is written here rather than by the
 * server because "in about 12 min" depends on the clock it is read against,
 * and a phone resuming from a pocket holds an answer from earlier.
 */

import type { NowModel, NowReading, NowStep } from '../api/types'
import { RADAR_MAX_AGE_MS } from './now'

export const STEP_MINUTES = 2
/** One bar per two minutes for an hour. */
export const BARS = 30
/** Fewer than this many bars ahead of now and the section says nothing. */
const MIN_BARS = 25
/**
 * The sentence ignores a run of wet or dry bars shorter than this. Two
 * minutes of light rain at minute 40, or a two-minute gap in a shield, is
 * drawn but not announced: the sentence is the glance, and a shower line
 * flickering across the floor must not turn it into a list.
 */
const MIN_RUN = 2

export interface Bar extends NowStep {
  /** When this bar starts, epoch ms. */
  at: number
}

/**
 * The thirty bars from now, or null when the section should not be there at
 * all: no reading, no next hour (cold start, off the grid), no coverage at
 * the point, or a frame too old to say anything with — the same rule as the
 * hero's, because a stale radar overrides nothing and forecasts nothing.
 */
export function nextHourBars(reading: NowReading | null | undefined, nowMs: number): Bar[] | null {
  if (!reading?.minutes || !reading.coverage) return null
  const frame = Date.parse(reading.time)
  if (!Number.isFinite(frame) || nowMs - frame > RADAR_MAX_AGE_MS) return null
  // The bar a minute in progress belongs to is the first one.
  const bars = reading.minutes
    .map((step) => ({ ...step, at: frame + step.lead * 60_000 }))
    .filter((bar) => bar.at + STEP_MINUTES * 60_000 > nowMs)
    .slice(0, BARS)
  return bars.length >= MIN_BARS ? bars : null
}

/** The sentence's idea of wet: a short run is absorbed by what is around it. */
function smoothed(bars: Bar[]): boolean[] {
  const wet = bars.map((b) => b.precipitating)
  const out = [...wet]
  let i = 0
  while (i < wet.length) {
    let j = i
    while (j < wet.length && wet[j] === wet[i]) j++
    // A short run between two others flips. One at either end stays: the
    // first bar is now, which is never "too short" to be true.
    if (j - i < MIN_RUN && i > 0 && j < wet.length) out.fill(!wet[i], i, j)
    i = j
  }
  return out
}

const NOUNS: Record<NonNullable<NowStep['type']>, string> = {
  rain: 'Rain',
  showers: 'Showers',
  snow: 'Snow',
  hail: 'Rain and hail',
}

/** "in a few minutes", "in about 8 min", "in about 25 min". */
function inAbout(minutes: number): string {
  if (minutes < 5) return 'in a few minutes'
  return `in about ${minutes < 15 ? Math.round(minutes) : Math.round(minutes / 5) * 5} min`
}

function laterBy(minutes: number): string {
  return `${minutes < 15 ? Math.round(minutes) : Math.round(minutes / 5) * 5} min later`
}

function strongest(bars: Bar[]): Bar {
  return bars.reduce((a, b) => (b.level > a.level ? b : a))
}

/**
 * Whether HRRR has rain forming near the point in the hour from now (nowcast
 * Stage D): any of its 15-minute steps inside the hour wet within its radius.
 * Asked against the clock here for the reason the sentence is: the answer
 * was written when the frame was, and a phone resumes from a pocket.
 */
export function modelDevelops(model: NowModel | null | undefined, nowMs: number): boolean {
  if (!model) return false
  return model.steps.some((step) => {
    const at = Date.parse(step.valid)
    return step.wet && at >= nowMs && at <= nowMs + BARS * STEP_MINUTES * 60_000
  })
}

/**
 * One sentence about the hour: whether it is precipitating now, and the one
 * change worth knowing about. "Heavy rain starting in about 10 min", "Rain
 * ending in about 25 min", "No precipitation in the next hour".
 *
 * Rain from beyond the radars is unknown, not dry, so the horizon stops at
 * the first bar without coverage and the sentence says how far it can see.
 *
 * `brief` keeps the first change and drops the second ("…, stopping 30 min
 * later"), for the phone's collapsed header, which has one line at 375 px.
 *
 * `develops` is `modelDevelops`: the radar's hour is the radar slid along its
 * own motion, which cannot start a shower (plan §6), so when HRRR has one
 * forming nearby a dry hour is "No precipitation moving in" rather than none
 * at all. It changes only the dry sentence; the radar's own rain always wins.
 */
export function nextHourSentence(bars: Bar[], nowMs: number, brief = false, develops = false): string {
  const seen = bars.findIndex((b) => !b.coverage)
  const known = seen === -1 ? bars : bars.slice(0, seen)
  if (known.length === 0) return 'No radar coverage upstream'
  const wet = smoothed(known)
  const minutesTo = (i: number) => Math.max((known[i].at - nowMs) / 60_000, 0)
  const horizon = Math.round((known[known.length - 1].at + STEP_MINUTES * 60_000 - nowMs) / 60_000)
  const whole = seen === -1
  // Coverage that ends inside the bar in progress is none worth a sentence:
  // "for at least 0 min" is a number that says nothing.
  if (!whole && Math.round(horizon / 5) * 5 === 0) return 'No radar coverage upstream'
  const span = whole ? 'the next hour' : `at least ${Math.round(horizon / 5) * 5} min`
  const change = (from: number) => {
    const at = wet.findIndex((w, i) => i > from && w !== wet[from])
    return at === -1 ? null : at
  }

  if (wet[0]) {
    const now = known.find((b) => b.precipitating) ?? known[0]
    const noun = NOUNS[now.type ?? 'rain']
    const end = change(0)
    if (end === null) return whole ? `${noun} for the next hour` : `${noun} for ${span}`
    const again = change(end)
    if (again === null || brief) return `${noun} ending ${inAbout(minutesTo(end))}`
    return `${noun} stopping ${inAbout(minutesTo(end))}, starting again ${laterBy(minutesTo(again) - minutesTo(end))}`
  }

  const start = change(0)
  if (start === null && develops) {
    return whole ? 'No precipitation moving in' : `No precipitation moving in for ${span}`
  }
  if (start === null) return whole ? 'No precipitation in the next hour' : `No precipitation for ${span}`
  const stop = change(start)
  const spell = known.slice(start, stop ?? known.length).filter((b) => b.precipitating)
  const label = (spell.length ? strongest(spell) : known[start]).label ?? 'Precipitation'
  const opening = `${label} starting ${inAbout(minutesTo(start))}`
  return stop === null || brief ? opening : `${opening}, stopping ${laterBy(minutesTo(stop) - minutesTo(start))}`
}

/**
 * The line under a dry sentence that HRRR has qualified: what the model
 * shows and why the radar cannot. Words, not a rate, because "about 12 mi"
 * is already as much as a neighbourhood check can claim.
 */
export function developsNote(model: NowModel): string {
  const miles = Math.round(model.radius_km / 1.609)
  return `The HRRR model has some forming within ${miles} mi. Radar shows only what has already formed.`
}

/** Whether the section is only its sentence: nothing to announce, nothing to draw. */
export function isDryHour(bars: Bar[]): boolean {
  return !smoothed(bars).some(Boolean) && !bars.some((b) => !b.coverage)
}

/** A bar's height, 0 to 1: its place on the intensity scale, and a sliver at least when wet. */
export function barHeight(bar: Bar): number {
  return bar.precipitating ? Math.max(bar.level, 0.06) : 0
}

const COMPASS = ['N', 'NNE', 'NE', 'ENE', 'E', 'ESE', 'SE', 'SSE', 'S', 'SSW', 'SW', 'WSW', 'W', 'WNW', 'NW', 'NNW']

/** "Moving ENE at 19 mph", or null when it is barely moving. */
export function motionLine(motion: NowReading['motion']): string | null {
  if (!motion) return null
  const mph = Math.round(motion.speed_kmh / 1.609)
  if (mph < 3) return 'Barely moving'
  const point = COMPASS[Math.round(motion.toward_deg / 22.5) % 16]
  return `Moving ${point} at ${mph} mph`
}

/**
 * What a tapped bar says: its time, its words and, for rain, its rate. Snow
 * gets words only until the snow-to-liquid ratio exists (plan, "Snowfall
 * rate"), the same rule as the station line's.
 */
export function barReadout(bar: Bar, timeZone?: string): string {
  const time = new Date(bar.at).toLocaleTimeString([], {
    hour: 'numeric',
    minute: '2-digit',
    ...(timeZone ? { timeZone } : {}),
  })
  if (!bar.coverage) return `${time} · beyond the radars`
  if (!bar.precipitating || !bar.label) return `${time} · none`
  const rate = bar.type !== 'snow' && bar.rate_in_h != null ? ` · ${bar.rate_in_h.toFixed(2)} in/h` : ''
  return `${time} · ${bar.label}${rate}`
}
